import { useState } from 'react';
import clsx from 'clsx';
import { cancelOrder, OrderConflictError, type OrderDetail } from '../../lib/orders';
import { CANCEL_REASONS } from '../../lib/order-edit-rules';

interface CancelOrderDialogProps {
  order: OrderDetail;
  isOpen: boolean;
  onClose: () => void;
  onCancelled: (nieuweLastModifiedDate: string | null) => void;
}

const DEFAULT_REASON = CANCEL_REASONS[0]?.code ?? 'anders';

export function CancelOrderDialog({ order, isOpen, onClose, onCancelled }: CancelOrderDialogProps) {
  const [reasonCode, setReasonCode] = useState<string>(DEFAULT_REASON);
  const [freeText, setFreeText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    const reason =
      reasonCode === 'anders' && freeText.trim()
        ? `anders: ${freeText.trim()}`
        : reasonCode;
    setSubmitting(true);
    setError(null);
    try {
      // Geen expectedLastModifiedDate (zie EditableOrderItems voor reden).
      const result = await cancelOrder(order.id, reason);
      onCancelled(result.nieuweLastModifiedDate);
      onClose();
    } catch (err) {
      if (err instanceof OrderConflictError) {
        setError('Deze order is ondertussen gewijzigd. Ververs de pagina en probeer opnieuw.');
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Annuleren mislukt');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Order annuleren</h2>
          <p className="text-xs text-slate-500 mt-1">Ordernummer: {order.orderNumber}</p>
        </div>

        <div className="px-6 py-4 space-y-3">
          <label className="block">
            <span className="text-sm text-slate-700">Reden</span>
            <select
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
              disabled={submitting}
            >
              {CANCEL_REASONS.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>

          {reasonCode === 'anders' && (
            <label className="block">
              <span className="text-sm text-slate-700">Toelichting</span>
              <textarea
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                rows={3}
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                placeholder="Korte beschrijving…"
                disabled={submitting}
              />
            </label>
          )}

          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
            Dit kan niet ongedaan worden gemaakt via de office-app. Een nieuwe order
            moet handmatig worden aangemaakt als de klant alsnog doorgaat.
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || (reasonCode === 'anders' && !freeText.trim())}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              submitting
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700'
            )}
          >
            {submitting ? 'Annuleren…' : 'Order annuleren'}
          </button>
        </div>
      </div>
    </div>
  );
}
